import { EXPENSE_RATIOS, AVG_NIGHTLY_RATES_BY_LOCATION, RENOVATION_PHASES } from '../constants/realEstateConstants';

export const PROJECTION_YEARS = 5;
// Annualizing a 1-2 month hold compounds the period return 6-12x and produces
// absurd figures, so the annualization horizon is floored.
export const MIN_ANNUALIZATION_MONTHS = 3;

// What cash earns elsewhere when it is not in this property, a year.
export const DEFAULT_REINVESTMENT_RATE = 6;
// Short-term rental occupancy, in percent, when none is entered.
export const DEFAULT_OCCUPANCY = 65;

export const GROWTH_RATES = {
  rent: 0.025,
  propertyTax: 0.02,
  insurance: 0.04,
  maintenance: 0.03,
  otherExpenses: 0.03,
};

// Parses a form value without discarding an explicit 0 (which `x || default` does).
export function num(value, fallback = 0) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

const clamp = (value, lo, hi) => Math.min(hi, Math.max(lo, value));
const override = (value, fallback) => (Number.isFinite(value) ? value : fallback);

export function estimateNightlyRate(formData) {
  const sqft = num(formData.houseSize, 1500) || 1500;
  const baseRate = AVG_NIGHTLY_RATES_BY_LOCATION[formData.location || 'TX'] || 150;
  const sizeMultiplier = clamp(sqft / 1500, 0.8, 1.5);
  return Math.round(baseRate * sizeMultiplier);
}

export const nightlyRateEntered = (formData) => num(formData.nightlyRate, 0) > 0;

function resolveNightlyRate(formData) {
  const entered = num(formData.nightlyRate, 0);
  return entered > 0 ? entered : estimateNightlyRate(formData);
}

export function baseSensitivityInputs(formData) {
  return {
    purchasePrice: Math.max(0, num(formData.purchasePrice)),
    renovationCost: Math.max(0, num(formData.renovationCost)),
    arv: Math.max(0, num(formData.expectedSellingPrice)),
    holdingPeriod: Math.max(0, num(formData.holdingPeriod, 6)),
    interestRate: Math.max(0, num(formData.interestRate)),
    monthlyRent: Math.max(0, num(formData.expectedMonthlyRent)),
    occupancyRate: clamp(num(formData.occupancyRate, DEFAULT_OCCUPANCY), 0, 100),
    nightlyRate: resolveNightlyRate(formData),
    reinvestmentRate: clamp(num(formData.reinvestmentRate, DEFAULT_REINVESTMENT_RATE), 0, 30),
  };
}

export function calculateMonthlyPayment(principal, monthlyRate, totalPayments) {
  if (!(principal > 0) || !(totalPayments > 0)) return 0;
  if (monthlyRate === 0) return principal / totalPayments;
  const growth = Math.pow(1 + monthlyRate, totalPayments);
  return principal * (monthlyRate * growth) / (growth - 1);
}

export function calculateRemainingLoanBalance(principal, monthlyRate, totalPayments, paymentsMade) {
  if (!(principal > 0) || !(totalPayments > 0) || paymentsMade >= totalPayments) return 0;
  if (paymentsMade <= 0) return principal;
  if (monthlyRate === 0) return principal * (1 - paymentsMade / totalPayments);
  const growth = Math.pow(1 + monthlyRate, totalPayments);
  return principal * (growth - Math.pow(1 + monthlyRate, paymentsMade)) / (growth - 1);
}

function annualize(totalRoiPct, years) {
  if (!(years > 0)) return 0;
  return (1 + totalRoiPct / 100) > 0
    ? (Math.pow(1 + totalRoiPct / 100, 1 / years) - 1) * 100
    : -100;
}

function buildRenovationTimeline(formData, renovationCost) {
  const phases = RENOVATION_PHASES[formData.houseCondition || 'fair'] || RENOVATION_PHASES.fair;
  const totalWeeks = phases.reduce((sum, p) => sum + p.weeks, 0);
  let cumulativeWeeks = 0;
  let cumulativeCost = 0;
  return phases.map((phase) => {
    cumulativeWeeks += phase.weeks;
    cumulativeCost += renovationCost * phase.costPct;
    return {
      phase: phase.phase,
      weeks: cumulativeWeeks,
      cost: Math.round(cumulativeCost),
      weeklyProgress: Math.round((cumulativeWeeks / totalWeeks) * 100),
    };
  });
}

/**
 * Single source of truth for every real estate figure shown in the app.
 * `overrides` carries the sensitivity-slider values; any non-finite entry
 * falls back to the form value.
 */
export function analyzeDeal(formData, overrides = {}) {
  const base = baseSensitivityInputs(formData);
  const purchasePrice = Math.max(0, override(overrides.purchasePrice, base.purchasePrice));
  const renovationCost = Math.max(0, override(overrides.renovationCost, base.renovationCost));
  const expectedSellingPrice = Math.max(0, override(overrides.arv, base.arv));
  const holdingPeriod = Math.max(0, override(overrides.holdingPeriod, base.holdingPeriod));
  const interestRate = Math.max(0, override(overrides.interestRate, base.interestRate));
  const monthlyRent = Math.max(0, override(overrides.monthlyRent, base.monthlyRent));
  const occupancyRate = clamp(override(overrides.occupancyRate, base.occupancyRate), 0, 100);
  const nightlyRate = Math.max(0, override(overrides.nightlyRate, base.nightlyRate));
  const reinvestmentRate = clamp(override(overrides.reinvestmentRate, base.reinvestmentRate), 0, 30);

  // Financing
  const downPaymentPercent = clamp(num(formData.downPayment, 20), 0, 100);
  const downPayment = (purchasePrice * downPaymentPercent) / 100;
  const loanAmount = purchasePrice - downPayment;
  const monthlyInterestRate = interestRate / 100 / 12;
  const loanTermYears = num(formData.loanTerm, 30) > 0 ? num(formData.loanTerm, 30) : 30;
  const numberOfPayments = loanTermYears * 12;
  const closingCostPercent = Math.max(0, num(formData.closingCosts, 3));
  const closingCosts = (purchasePrice * closingCostPercent) / 100;
  const monthlyPayment = calculateMonthlyPayment(loanAmount, monthlyInterestRate, numberOfPayments);
  const balanceAfter = (payments) =>
    calculateRemainingLoanBalance(loanAmount, monthlyInterestRate, numberOfPayments, payments);
  const initialInvestment = downPayment + renovationCost + closingCosts;

  // Property tax and insurance are the same bills whichever strategy is used,
  // so the flip and both rentals read them from here. Left blank, they are
  // estimated from the purchase price.
  const enteredPropertyTax = num(formData.annualPropertyTax, 0);
  const enteredInsurance = num(formData.annualInsurance, 0);
  const propertyTaxEstimated = !(enteredPropertyTax > 0);
  const insuranceEstimated = !(enteredInsurance > 0);
  const propertyTaxAnnual = propertyTaxEstimated ? purchasePrice * EXPENSE_RATIOS.propertyTax : enteredPropertyTax;
  const insuranceAnnual = insuranceEstimated ? purchasePrice * EXPENSE_RATIOS.insurance : enteredInsurance;
  // HOA dues are owed whether the house is empty, let long-term or let by the night.
  const monthlyHoa = Math.max(0, num(formData.monthlyHoa));
  const hoaAnnual = monthlyHoa * 12;

  // Fix & flip. Only interest is a cost of holding: principal repaid during the
  // hold comes back at sale, so it counts as cash invested but not as an expense.
  // `monthlyExpenses` is everything else paid while the house sits empty.
  const monthlyExpenses = Math.max(0, num(formData.monthlyExpenses));
  const monthlyCarry = monthlyExpenses + monthlyHoa + (propertyTaxAnnual + insuranceAnnual) / 12;
  const otherHoldingCosts = monthlyCarry * holdingPeriod;
  const sellingCostPercent = clamp(num(formData.sellingCosts, 6), 0, 100);
  const sellingCosts = (expectedSellingPrice * sellingCostPercent) / 100;

  // A flip is usually financed differently from a rental. 'hardMoney' is a
  // short interest-only loan against the purchase and the renovation budget,
  // with points paid up front; 'cash' uses no loan; anything else reuses the
  // long-term loan above.
  const flipFinancing = ['hardMoney', 'cash'].includes(formData.flipFinancing) ? formData.flipFinancing : 'same';
  let flipLoanAmount = loanAmount;
  let flipPoints = 0;
  let interestDuringHold;
  let principalRepaidDuringHold = 0;
  let flipCashToClose;
  if (flipFinancing === 'hardMoney') {
    const ltv = clamp(num(formData.hardMoneyLtv, 85), 0, 100) / 100;
    const rehabFunded = clamp(num(formData.hardMoneyRehabFunded, 100), 0, 100) / 100;
    flipLoanAmount = purchasePrice * ltv + renovationCost * rehabFunded;
    flipPoints = flipLoanAmount * clamp(num(formData.hardMoneyPoints, 2), 0, 20) / 100;
    // Interest is charged on the full commitment from day one: slightly
    // conservative where the lender releases the renovation money in draws.
    interestDuringHold = flipLoanAmount * (Math.max(0, num(formData.hardMoneyRate, 11)) / 100) * (holdingPeriod / 12);
    flipCashToClose = purchasePrice + renovationCost - flipLoanAmount + closingCosts + flipPoints;
  } else if (flipFinancing === 'cash') {
    flipLoanAmount = 0;
    interestDuringHold = 0;
    flipCashToClose = purchasePrice + renovationCost + closingCosts;
  } else {
    const paymentsDuringHold = Math.min(holdingPeriod, numberOfPayments);
    principalRepaidDuringHold = loanAmount - balanceAfter(paymentsDuringHold);
    interestDuringHold = Math.max(0, monthlyPayment * paymentsDuringHold - principalRepaidDuringHold);
    flipCashToClose = initialInvestment;
  }
  const totalHoldingCosts = interestDuringHold + otherHoldingCosts;

  const totalInvestment = flipCashToClose + totalHoldingCosts + principalRepaidDuringHold;
  const netProfit = expectedSellingPrice - sellingCosts - purchasePrice - renovationCost - closingCosts - flipPoints - totalHoldingCosts;
  // The 70% rule of thumb: pay no more than 70% of after-repair value, less repairs.
  const maxOfferSeventyRule = Math.max(0, expectedSellingPrice * 0.7 - renovationCost);
  const flipMargin = expectedSellingPrice > 0 ? netProfit / expectedSellingPrice : 0;
  const roi = totalInvestment > 0 ? (netProfit / totalInvestment) * 100 : 0;
  const annualizationMonths = Math.max(holdingPeriod, MIN_ANNUALIZATION_MONTHS);
  const annualizedROI = holdingPeriod > 0 ? annualize(roi, annualizationMonths / 12) : 0;

  const arvToPurchaseRatio = purchasePrice > 0 ? expectedSellingPrice / purchasePrice : 0;
  const renovationToArvRatio = expectedSellingPrice > 0 ? (renovationCost / expectedSellingPrice) * 100 : 0;

  const dealQuality = (() => {
    if (roi >= 30 && arvToPurchaseRatio >= 1.3 && renovationToArvRatio <= 20) return 'Excellent';
    if (roi >= 20 && arvToPurchaseRatio >= 1.2 && renovationToArvRatio <= 25) return 'Good';
    if (roi >= 10 && arvToPurchaseRatio >= 1.1 && renovationToArvRatio <= 30) return 'Fair';
    return 'Poor';
  })();

  // Long-term rental, year 1
  const annualRent = monthlyRent * 12;
  const vacancyRate = clamp(num(formData.vacancyRate, 5), 0, 100);
  const effectiveAnnualRent = annualRent * (1 - vacancyRate / 100);
  const managementFeePercent = clamp(num(formData.propertyManagementFee, num(formData.propertyManagement, 8)), 0, 100);
  const propertyManagementFee = effectiveAnnualRent * managementFeePercent / 100;
  const maintenanceAnnual = purchasePrice * EXPENSE_RATIOS.maintenance;
  const utilitiesAnnual = purchasePrice * EXPENSE_RATIOS.utilities;
  const capexAnnual = annualRent * EXPENSE_RATIOS.capex;
  const totalAnnualExpenses = propertyTaxAnnual + insuranceAnnual + hoaAnnual + maintenanceAnnual + utilitiesAnnual + capexAnnual + propertyManagementFee;

  // Short-term rental, year 1
  const annualStrRevenue = nightlyRate * 365 * (occupancyRate / 100);
  const strManagementFeePercent = clamp(num(formData.strManagementFee, 20), 0, 100);
  const strManagementFee = annualStrRevenue * strManagementFeePercent / 100;
  const strPropertyTaxAnnual = propertyTaxAnnual;
  const strInsuranceAnnual = insuranceAnnual * 1.5;
  const strMaintenanceAnnual = purchasePrice * EXPENSE_RATIOS.strMaintenance;
  const strUtilitiesAnnual = purchasePrice * EXPENSE_RATIOS.strUtilities;
  const strCapexAnnual = annualStrRevenue * EXPENSE_RATIOS.strCapex;
  const additionalStrMonthly = Math.max(0, num(formData.additionalStrExpenses, 250));
  const additionalStrExpenses = additionalStrMonthly * 12;
  const cleaningPerTurn = Math.max(0, num(formData.cleaningPerTurn, 100));
  const averageStay = num(formData.averageStay, 3) > 0 ? num(formData.averageStay, 3) : 3;
  const strCleaningCosts = cleaningPerTurn * Math.ceil(365 * (occupancyRate / 100) / averageStay);
  const totalStrAnnualExpenses = strPropertyTaxAnnual + strInsuranceAnnual + hoaAnnual + strMaintenanceAnnual +
    strUtilitiesAnnual + strCapexAnnual + strManagementFee + strCleaningCosts + additionalStrExpenses;

  // A property being renovated cannot be rented. The flip carries its holding
  // period; the rentals lose the same months of income at the start of year 1,
  // while tax, insurance and the loan keep running.
  const renovationWeeks = (RENOVATION_PHASES[formData.houseCondition || 'fair'] || RENOVATION_PHASES.fair)
    .reduce((sum, phase) => sum + phase.weeks, 0);
  const renovationMonths = renovationCost > 0 ? Math.min(12, Math.round(renovationWeeks / 4.345)) : 0;
  const rentedShareOfYearOne = 1 - renovationMonths / 12;

  // Year-by-year projection. The chart and the 5-year profit both read from this.
  const projection = [];
  for (let year = 1; year <= PROJECTION_YEARS; year++) {
    const grow = (rate) => Math.pow(1 + rate, year - 1);
    const rented = year === 1 ? rentedShareOfYearOne : 1;
    const paymentsThisYear = clamp(numberOfPayments - 12 * (year - 1), 0, 12);
    const debtService = monthlyPayment * paymentsThisYear;
    const tax = propertyTaxAnnual * grow(GROWTH_RATES.propertyTax);
    const rentGrowth = grow(GROWTH_RATES.rent);
    const other = grow(GROWTH_RATES.otherExpenses);
    const hoa = hoaAnnual * other;

    // Costs that follow occupancy scale with the months rented; the rest do not.
    const ltrRevenue = effectiveAnnualRent * rentGrowth * rented;
    const ltrExpenses = tax + hoa +
      insuranceAnnual * grow(GROWTH_RATES.insurance) +
      (maintenanceAnnual * grow(GROWTH_RATES.maintenance) + utilitiesAnnual * other + capexAnnual * rentGrowth) * rented +
      ltrRevenue * managementFeePercent / 100;

    const strRevenue = annualStrRevenue * rentGrowth * rented;
    const strExpenses = tax + hoa +
      strInsuranceAnnual * grow(GROWTH_RATES.insurance) +
      (strMaintenanceAnnual * grow(GROWTH_RATES.maintenance) + strUtilitiesAnnual * other + strCapexAnnual * rentGrowth +
        (strCleaningCosts + additionalStrExpenses) * other) * rented +
      strRevenue * strManagementFeePercent / 100;

    projection.push({
      year,
      ltr: ltrRevenue - ltrExpenses - debtService,
      str: strRevenue - strExpenses - debtService,
    });
  }

  const annualDebtService = monthlyPayment * Math.min(12, numberOfPayments);
  // "Annual cash flow" is a full rented year at today's rents and costs. The
  // first projected year is lower when part of it is spent renovating.
  const annualCashFlow = effectiveAnnualRent - totalAnnualExpenses - annualDebtService;
  const annualStrCashFlow = annualStrRevenue - totalStrAnnualExpenses - annualDebtService;
  const firstYearCashFlow = projection[0].ltr;
  const firstYearStrCashFlow = projection[0].str;
  const monthlyCashFlow = annualCashFlow / 12;
  const ltrCashOnCashReturn = initialInvestment > 0 ? (annualCashFlow / initialInvestment) * 100 : 0;
  const strCashOnCashReturn = initialInvestment > 0 ? (annualStrCashFlow / initialInvestment) * 100 : 0;
  // Income after expenses over what the property cost to buy and put in rentable condition.
  const capRate = purchasePrice + renovationCost > 0 ? (effectiveAnnualRent - totalAnnualExpenses) / (purchasePrice + renovationCost) * 100 : 0;
  const ltrFiveYearCashFlow = projection.reduce((sum, y) => sum + y.ltr, 0);
  const strFiveYearCashFlow = projection.reduce((sum, y) => sum + y.str, 0);

  // Debt-service coverage: operating income divided by loan payments. Below 1
  // the rent does not cover the loan and the owner pays the difference.
  // null means there is no loan to cover.
  const coverage = (income) => (annualDebtService > 0 ? income / annualDebtService : null);
  const ltrOperatingIncome = effectiveAnnualRent - totalAnnualExpenses;
  const strOperatingIncome = annualStrRevenue - totalStrAnnualExpenses;
  const ltrCoverage = coverage(ltrOperatingIncome);
  const strCoverage = coverage(strOperatingIncome);
  const coverageScore = (ratio, income) => (ratio == null
    ? (income > 0 ? 10 : 0)
    : clamp(((ratio - 0.8) / 0.7) * 10, 0, 10));
  // Risk scores out of 10 (higher is safer), derived from the deal itself:
  // rentals from how well income covers the loan, with a deduction for the
  // less predictable short-term rental income; a flip from its profit cushion
  // and how long the money is exposed.
  const riskScores = {
    ltr: coverageScore(ltrCoverage, ltrOperatingIncome),
    str: Math.max(0, coverageScore(strCoverage, strOperatingIncome) - 2),
    flip: clamp((flipMargin / 0.2) * 10 - holdingPeriod / 3, 0, 10),
  };
  const riskLabel = (score) => (score >= 7 ? 'Lower' : score >= 4 ? 'Moderate' : 'Higher');
  const riskLabels = { ltr: riskLabel(riskScores.ltr), str: riskLabel(riskScores.str), flip: riskLabel(riskScores.flip) };

  // Exit after the projection. When renovation money goes in, the rental is
  // valued from the same after-repair value the flip sells at. With no
  // renovation there is no forced appreciation, so it is valued at what was paid.
  const annualAppreciationPercent = Math.max(-100, num(formData.annualAppreciation, 3));
  const annualAppreciationRate = annualAppreciationPercent / 100;
  const exitValueBasis = renovationCost > 0 && expectedSellingPrice > 0 ? expectedSellingPrice : purchasePrice;
  const futureValue = exitValueBasis * Math.pow(1 + annualAppreciationRate, PROJECTION_YEARS);
  const futureSellingCosts = futureValue * sellingCostPercent / 100;
  const remainingLoanBalance = balanceAfter(PROJECTION_YEARS * 12);
  const loanPaydown = loanAmount - remainingLoanBalance;
  const valueGain = futureValue - purchasePrice;
  // Split the gain into what the renovation created and what the market added.
  const forcedEquity = exitValueBasis - purchasePrice - renovationCost;
  const marketAppreciation = futureValue - exitValueBasis;
  const netSaleProceeds = futureValue - futureSellingCosts - remainingLoanBalance;

  // Equivalent to: cash flow + value gain + loan paydown - renovation - closing - selling costs.
  const rentalProfit = ltrFiveYearCashFlow + netSaleProceeds - initialInvestment;
  const strTotalProfit = strFiveYearCashFlow + netSaleProceeds - initialInvestment;
  const rentalROIValue = initialInvestment > 0 ? (rentalProfit / initialInvestment) * 100 : 0;
  const strTotalROI = initialInvestment > 0 ? (strTotalProfit / initialInvestment) * 100 : 0;
  const ltrTotalROIAnnualized = annualize(rentalROIValue, PROJECTION_YEARS);
  const strTotalROIAnnualized = annualize(strTotalROI, PROJECTION_YEARS);

  // The same five years, on the same footing. Annualized returns cannot settle
  // "flip now or hold": a flip frees its cash after a few months and a rental
  // ties it up for five years. Here every strategy starts with the same cash,
  // anything not in the property earns the reinvestment rate, and the measure
  // is what the investor has at the end of year five.
  const reinvest = reinvestmentRate / 100;
  const compound = (years) => Math.pow(1 + reinvest, Math.max(0, years));
  const startingCash = Math.max(totalInvestment, initialInvestment);
  const flipYears = Math.min(PROJECTION_YEARS, holdingPeriod / 12);
  const flipEndingWealth = (totalInvestment + netProfit) * compound(PROJECTION_YEARS - flipYears) +
    (startingCash - totalInvestment) * compound(PROJECTION_YEARS);
  // Yearly cash flow is banked (or, when negative, drawn from the same money).
  const carried = (key) => projection.reduce((sum, y) => sum + y[key] * compound(PROJECTION_YEARS - y.year), 0);
  const sideFund = (startingCash - initialInvestment) * compound(PROJECTION_YEARS);
  const holdEndingWealth = (key) => netSaleProceeds + carried(key) + sideFund;
  // Appreciation at which holding ends level with flipping and reinvesting.
  const breakEvenAppreciation = (key) => {
    const saleShare = exitValueBasis * (1 - sellingCostPercent / 100);
    if (!(saleShare > 0)) return null;
    const neededValueGrowth = (flipEndingWealth - carried(key) - sideFund + remainingLoanBalance) / saleShare;
    return neededValueGrowth > 0 ? (Math.pow(neededValueGrowth, 1 / PROJECTION_YEARS) - 1) * 100 : -100;
  };
  // Where a rental's five-year profit comes from. Sums to the profit.
  const profitSources = (cashFlow) => ({
    renovationValue: forcedEquity,
    marketAppreciation,
    loanPaydown,
    cashFlow,
    costs: -(closingCosts + futureSellingCosts),
  });
  const yearlyReturn = (endingWealth) => (startingCash > 0 && endingWealth > 0
    ? (Math.pow(endingWealth / startingCash, 1 / PROJECTION_YEARS) - 1) * 100
    : (startingCash > 0 ? -100 : 0));
  // The same cash never put into a property: it earns the reinvestment rate throughout.
  const leaveInvestedWealth = startingCash * compound(PROJECTION_YEARS);
  const sameHorizon = {
    years: PROJECTION_YEARS,
    reinvestmentRate,
    startingCash,
    leaveInvested: { endingWealth: leaveInvestedWealth, gain: leaveInvestedWealth - startingCash, annualReturn: yearlyReturn(leaveInvestedWealth) },
    flip: { endingWealth: flipEndingWealth, gain: flipEndingWealth - startingCash, annualReturn: yearlyReturn(flipEndingWealth) },
    ltr: {
      endingWealth: holdEndingWealth('ltr'),
      gain: holdEndingWealth('ltr') - startingCash,
      annualReturn: yearlyReturn(holdEndingWealth('ltr')),
      breakEvenAppreciation: breakEvenAppreciation('ltr'),
      profitSources: profitSources(ltrFiveYearCashFlow),
    },
    str: {
      endingWealth: holdEndingWealth('str'),
      gain: holdEndingWealth('str') - startingCash,
      annualReturn: yearlyReturn(holdEndingWealth('str')),
      breakEvenAppreciation: breakEvenAppreciation('str'),
      profitSources: profitSources(strFiveYearCashFlow),
    },
  };

  return {
    sameHorizon,
    purchasePrice, downPayment, downPaymentPercent, loanAmount, interestRate, loanTermYears,
    monthlyInterestRate, numberOfPayments, monthlyPayment, annualDebtService,
    closingCosts, closingCostPercent, initialInvestment,
    renovationCost, holdingPeriod, monthlyExpenses, monthlyCarry,
    propertyTaxEstimated, insuranceEstimated, nightlyRateEstimated: !nightlyRateEntered(formData),
    cleaningPerTurn, averageStay,
    interestDuringHold, otherHoldingCosts, totalHoldingCosts, principalRepaidDuringHold,
    flipFinancing, flipLoanAmount, flipPoints, flipCashToClose, maxOfferSeventyRule, flipMargin,
    ltrCoverage, strCoverage, riskScores, riskLabels, forcedEquity, marketAppreciation,
    expectedSellingPrice, sellingCosts, sellingCostPercent, totalInvestment, netProfit, roi,
    annualizationMonths, annualizedROI, arvToPurchaseRatio, renovationToArvRatio, dealQuality,
    monthlyRent, annualRent, vacancyRate, effectiveAnnualRent, managementFeePercent, propertyManagementFee,
    propertyTaxAnnual, insuranceAnnual, monthlyHoa, hoaAnnual, maintenanceAnnual, utilitiesAnnual, capexAnnual,
    totalAnnualExpenses, annualCashFlow, monthlyCashFlow, ltrCashOnCashReturn, capRate,
    nightlyRate, occupancyRate, annualStrRevenue, strManagementFeePercent, strManagementFee,
    strPropertyTaxAnnual, strInsuranceAnnual, strMaintenanceAnnual, strUtilitiesAnnual,
    strCapexAnnual, strCleaningCosts, additionalStrMonthly, additionalStrExpenses,
    totalStrAnnualExpenses, annualStrCashFlow, strCashOnCashReturn,
    annualAppreciationPercent, annualAppreciationRate, exitValueBasis, futureValue, valueGain,
    futureSellingCosts, remainingLoanBalance, loanPaydown, netSaleProceeds,
    ltrFiveYearCashFlow, strFiveYearCashFlow,
    renovationMonths, firstYearCashFlow, firstYearStrCashFlow,
    rentalProfit, rentalROIValue, ltrTotalROIAnnualized,
    strTotalProfit, strTotalROI, strTotalROIAnnualized,
    cashFlowProjectionData: [
      { year: 'Year 0', ltr: 0, str: 0 },
      ...projection.map((y) => ({ year: `Year ${y.year}`, ltr: Math.round(y.ltr), str: Math.round(y.str) })),
    ],
    renovationTimelineData: buildRenovationTimeline(formData, renovationCost),
    // Where the flip's cash goes. Sums to totalInvestment.
    investmentBreakdown: [
      { name: 'Cash to Close', value: flipCashToClose - closingCosts - flipPoints },
      { name: 'Closing Costs', value: closingCosts },
      { name: 'Loan Points', value: flipPoints },
      { name: 'Holding Costs', value: totalHoldingCosts },
      { name: 'Principal Repaid', value: principalRepaidDuringHold },
    ].filter((item) => item.value > 0),
  };
}
